import * as React from 'react'
import dynamic from 'next/dynamic'

import { i18n, useConfig } from '@thg-commerce/enterprise-core'
import { Button, Skeleton } from '@thg-commerce/gravity-elements'
import { spacing, styled } from '@thg-commerce/gravity-theme'

const BellIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Bell'),
)
const DisableBellIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/BellDisable'),
)

export const StyledButton = styled(Button)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
`

const ButtonSkeleton = styled(Skeleton)`
  width: 100%;
  height: 50px;
`

enum WebViewMessageType {
  CheckNotifyWhenInStockEnabled = 'BIS_NOTIFICATION_ON_LOAD_PRODUCT',
  EnableNotifyWhenInStock = 'BIS_NOTIFICATION_OPT_IN_PRODUCT',
  DisableNotifyWhenInStock = 'BIS_NOTIFICATION_OPT_OUT_PRODUCT',
}

declare global {
  interface Window {
    ReactNativeWebView?: { postMessage: (data: string) => void }
  }
}

export interface PushNotifyWhenInStockProps {
  updateProductVariants: (params: { sku: string; isOptedIn: boolean }) => void
  isOptedInForNotification?: boolean
  sku: string
}
export const PushNotifyWhenInStock = ({
  sku,
  updateProductVariants,
  isOptedInForNotification,
}: PushNotifyWhenInStockProps) => {
  const { siteDefinition } = useConfig()
  const i18nText = {
    notifyMeText: i18n('product.outofstocknotification.addtowaitlist.text'),
    removeNotificationText: i18n(
      'product.outofstocknotification.removefromwaitlist.text',
    ),
  }

  const handleMessage = React.useCallback(
    (event: MessageEvent<any>) => {
      if (event.origin !== `https://${siteDefinition.domain}`) {
        return
      }
      const eventData = JSON.parse(event.data)

      if (!eventData.eventCategory) {
        return
      }

      switch (eventData.eventCategory) {
        case WebViewMessageType.CheckNotifyWhenInStockEnabled:
        case WebViewMessageType.EnableNotifyWhenInStock:
          updateProductVariants({
            sku,
            isOptedIn: eventData.value,
          })
          break
        case WebViewMessageType.DisableNotifyWhenInStock:
          updateProductVariants({
            sku,
            isOptedIn: !eventData.value,
          })
          break

        default:
          break
      }
    },
    [sku, updateProductVariants, siteDefinition.domain],
  )

  const postMessage = React.useCallback(
    (eventCategory: WebViewMessageType) => {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          eventCategory,
          sku,
        }),
      )
    },
    [sku],
  )

  const checkProductOptIn = React.useCallback(() => {
    if (isOptedInForNotification !== undefined) {
      return
    }
    postMessage(WebViewMessageType.CheckNotifyWhenInStockEnabled)
  }, [postMessage, isOptedInForNotification])

  const onMessage = React.useCallback((message) => handleMessage(message), [
    handleMessage,
  ])

  React.useEffect(() => {
    checkProductOptIn()
    window.addEventListener('message', onMessage)

    return () => {
      window.removeEventListener('message', onMessage)
    }
  }, [sku, checkProductOptIn, onMessage])

  const handleButtonClick = () => {
    const eventCategory = isOptedInForNotification
      ? WebViewMessageType.DisableNotifyWhenInStock
      : WebViewMessageType.EnableNotifyWhenInStock

    postMessage(eventCategory)
  }

  if (isOptedInForNotification === undefined) {
    return <ButtonSkeleton />
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <StyledButton
        data-testid="add-device-to-waitlist"
        sizing="regular"
        emphasis="high"
        onClick={handleButtonClick}
      >
        {isOptedInForNotification ? (
          <React.Fragment>
            <DisableBellIcon style={{ marginRight: spacing(1) }} />
            <span>{i18nText.removeNotificationText}</span>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <BellIcon style={{ marginRight: spacing(1) }} />
            <span>{i18nText.notifyMeText}</span>
          </React.Fragment>
        )}
      </StyledButton>
    </div>
  )
}
